import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { isIframe } from '../../lib/is-iframe';
import { sleep } from '../../lib/sleep';
import { AppConfigService } from '../../providers/config.service';
import { BrowserService } from '../../services/browser.service';
import { DataLoaderService } from '../../services/data-loader.service';
import { StateService } from '../../services/state.service';
import { StorageService } from '../../services/storage.service';
import { ToastService } from '../../services/toast.service';

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Clerk } from '@clerk/clerk-js/headless';
import { ExternalAccountResource } from '@clerk/types';
import { filter, firstValueFrom, take } from 'rxjs';
import { GROWTHBOOK } from '../../growthbook';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { IGNORE_AUTH_ERRORS, NetworkInterceptorService } from '../../network-interceptor';
import { NetworkService } from '../../services/network.service';
import { SentryService } from '../../services/sentry.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigation: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private state = inject(StateService);
  private loader = inject(DataLoaderService);
  private config = inject(AppConfigService);
  private cookieService = inject(CookieService);
  private storage = inject(StorageService);
  private browserService = inject(BrowserService);
  private toasts = inject(ToastService);
  private zone = inject(NgZone);
  private networkService = inject(NetworkService);
  private sentryService = inject(SentryService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private platformId = inject<string>(PLATFORM_ID);
  private networkInterceptor = inject(NetworkInterceptorService);
  clerk: Clerk;
  isIframe: boolean;
  pageLoadFromClerkSync = false;
  private growthBook = inject(GROWTHBOOK);

  private errorWatcher = this.networkInterceptor.error$.pipe(filter(Boolean)).subscribe(async ({ error, req }) => {
    if (isPlatformServer(this.platformId)) return;
    const code = error?.error?.code;
    if (!['any_authentication_required', 'session_expired', 'forbidden_request'].includes(code)) return;
    if (req.context.get(IGNORE_AUTH_ERRORS)) return;
    const wasAuthenticatedAsUser = !!this.state.tokenData;
    const wasAuthenticatedAsGuest = !!this.state.guestAuthToken;
    this.storage.remove('guest-auth');
    this.storage.removeCookie('guest-auth');
    this.state.guestAuthToken = undefined;
    this.state.guest = undefined;
    this.sentryService.captureException('auth_error', {
      code,
      isRetry: !!this.storage.get('reloadedDueToAuthError'),
      wasAuthenticatedAsUser,
      wasAuthenticatedAsGuest,
    });
    await sleep(500);
    // eslint-disable-next-line no-debugger
    debugger;
    if (this.storage.get('reloadedDueToAuthError')) return this.logout();
    this.storage.set('reloadedDueToAuthError', true, true);
    this.browserService.reload(true);
  });

  private pageLoadWatcher = this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      take(1)
    )
    .subscribe(() => {
      if (isPlatformServer(this.platformId)) return;
      if (!this.storage.get('reloadedDueToAuthError')) return;
      setTimeout(() => {
        this.storage.remove('reloadedDueToAuthError');
      }, 1000 * 60);
    });

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return; // Don't do this on the server
    this.pageLoadFromClerkSync = location.search.includes('__clerk_synced=true');
    this.isIframe = isIframe();
    this.tokenInit();

    // intercepting clerk page navigation and routing it through angular router
    window.navigation?.addEventListener('navigate', event => {
      if (event.destination.sameDocument) return;
      if (event.navigationType === 'reload') return;
      if (!location.pathname.includes('/auth/')) return;

      const url = new URL(event.destination.url);
      if (!url.pathname.includes('/auth/')) return;
      if (url.pathname === '/auth/authorize') return;

      this.router.navigate(url.pathname.split('/'), {
        queryParams: url.searchParams,
        fragment: url.hash?.replace('#', '') || undefined,
      });
      event.preventDefault();
    });
  }

  reportedClerkError = false;

  private getClerkConfig() {
    // const onCustomDomain = !!this.browserService.getTenantCustomDomain();
    // const isSatellite = onCustomDomain && this.growthBook.isOn('custom-domain-auth');

    // const domain = isSatellite
    //   ? this.growthBook.getFeatureValue('custom-domain-domain-override', location.hostname)
    //   : undefined;

    const settings = {
      signInUrl: this.config.correctPicflowUrl + '/auth/login',
      signUpUrl: this.config.correctPicflowUrl + '/auth/signup',
      touchSession: false,
      standardBrowser: true,
      // isSatellite,
      polling: false,
    };

    return settings;
    // return { domain, isSatellite, settings };
  }

  private async clerkInit() {
    if (location.search.includes('preview=true') || this.state.galleryPreviewMode) {
      console.log('skipping clerk init in preview mode');
      return;
    }
    if (this.browserService.getTenantCustomDomain() && !this.growthBook.isOn('custom-domain-auth')) return;
    try {
      // const { domain, isSatellite, settings } = this.getClerkConfig();
      const settings = this.getClerkConfig();

      this.clerk = new Clerk(this.config.clerkKey);
      // this.clerk = new Clerk(this.config.clerkKey, { domain });

      await this.clerk.load(settings);

      this.state.impersonating = !!this.clerk.session?.actor;

      // This is how we find out if clerk will redirect to the sync url. To avoid flashing the page, we await here while clerk redirects us.
      // if (isSatellite && !this.clerk.session && !this.pageLoadFromClerkSync) {
      //   console.debug('Waiting for clerk to redirect to sync session');
      //   console.debug('this.clerk.loaded', this.clerk.loaded);
      //   console.debug('this.clerk.__internal_last_error', this.clerk.__internal_last_error);
      //   await sleep(10_000);
      // }
    } catch (err) {
      if (this.reportedClerkError) return;
      this.reportedClerkError = true;
      console.error('clerk failed to load', err);

      if (
        err.message === 'The operation is insecure.' ||
        err.message ===
          `SecurityError: Failed to read the 'localStorage' property from 'Window': Access is denied for this document.`
      ) {
        this.sentryService.captureException('clerk failed due to localStorage error');
        this.toasts.add('Failed to load authentication service due to browser security settings', 'error', {
          duration: 10_000,
        });
      } else {
        this.sentryService.captureException('clerk failed to load');
        this.toasts.add('Failed to load authentication service', 'error', { duration: 10_000 });
      }
    }
  }

  private async initClerkWithComponents() {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('clerkWithComponentsInit', async () => {
      // const { domain, settings } = this.getClerkConfig();
      const settings = this.getClerkConfig();
      const { Clerk } = await import('@clerk/clerk-js');
      this.clerk = new Clerk(this.config.clerkKey);
      // this.clerk = new Clerk(this.config.clerkKey, { domain });
      await this.clerk.load(settings);
      return true;
    });
  }

  async init() {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('authInit', async () => {
      return new Promise<boolean>(resolve => {
        this.zone.runOutsideAngular(async () => {
          await this.clerkInit();
          resolve(true);
        });
      });
    });
  }

  getTokenInitError() {
    if (isPlatformServer(this.platformId)) return 'server';
    if (this.isIframe && !this.browserService.isExtension()) return 'iframe';
    if (this.browserService.getTenantCustomDomain() && !this.growthBook.isOn('custom-domain-auth')) {
      return 'tenantDomain';
    }
  }

  async tokenInit() {
    if (this.getTokenInitError()) return;
    await this.init();
    return this.loader.get('authData', this.setupTokenData.bind(this));
  }

  public async mountSignUp(el: HTMLDivElement) {
    await this.initClerkWithComponents();
    this.clerk?.mountSignUp(el, {
      initialValues: { emailAddress: this.route.snapshot.queryParams.email },
      fallbackRedirectUrl: `${window.location.origin}/auth/authorize`,
      unsafeMetadata: { ref: this.storage.get('referralCode') },
      routing: 'path',
      path: getAllRouteData(this.route.snapshot).clerkBasePath || '/auth/signup',
    });
  }

  public async unmountSignUp(el: HTMLDivElement) {
    await this.initClerkWithComponents();
    this.clerk?.unmountSignUp(el);
  }

  public async mountSignIn(el: HTMLDivElement, returnUrl?: string) {
    await this.initClerkWithComponents();
    this.clerk?.mountSignIn(el, {
      fallbackRedirectUrl: `${window.location.origin}/auth/authorize?returnUrl=${encodeURIComponent(returnUrl || '')}`,
      routing: 'path',
      path: getAllRouteData(this.route.snapshot).clerkBasePath || '/auth/login',
      initialValues: { emailAddress: this.route.snapshot.queryParams.email || this.clerk.client.signIn?.identifier },
    });
  }

  public async unmountSignIn(el: HTMLDivElement) {
    await this.initClerkWithComponents();
    this.clerk?.unmountSignIn(el);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async logout(message?: string) {
    if (isPlatformServer(this.platformId)) return;
    await this.init();
    await this.clerk?.signOut();

    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');
    this.cookieService.delete('picflow-loggedin', '/');
    this.storage.remove('promo');
    this.storage.remove('referralCode');
    window.onbeforeunload = null;
    window.location.href = `${window.location.origin}/auth/login`;
  }

  private async setupTokenData() {
    const session = this.clerk?.session;
    if (!session) return false;

    this.state.tokenData = {
      getIdToken: () => this.getAuthToken(),
    };

    this.sentryService.setUser({ clerk_id: session.user?.id });
    return await session.getToken({ template: this.getDefaultTemplate() });
  }

  public async getTokenData() {
    await this.tokenInit();
    return this.state.tokenData;
  }

  private getDefaultTemplate() {
    return this.browserService.isExtension ? undefined : 'picflow_frontend';
  }

  private lastSkipCache = Date.now();
  public async getAuthToken(template = this.getDefaultTemplate(), skipCache = false, isRetry = false): Promise<string> {
    if (skipCache) {
      if (Date.now() - this.lastSkipCache < 1000) {
        console.debug('skipping cache too fast');
        skipCache = false;
      } else {
        this.lastSkipCache = Date.now();
      }
    }
    if (this.clerk.session) {
      return await this.clerk.session.getToken({ template, skipCache });
    }

    if (!isRetry) {
      await sleep(1000);
      await firstValueFrom(this.networkService.online$);
      return this.getAuthToken(template, skipCache, true);
    }

    console.debug('user after retry', this.clerk.user);
    this.sentryService.captureException('no_clerk_session');
    await sleep(100);
    // eslint-disable-next-line no-debugger
    debugger;
    this.browserService.reload(true);
    return;
  }

  public async getLinkedAccounts() {
    return this.loader.get('linkedAccounts', async () => {
      return this.clerk.user.externalAccounts;
    });
  }

  public async connectSocialAccount(provider: 'google' | 'apple') {
    const existingAccount = this.clerk.user.externalAccounts.find(a => a.provider === provider);
    if (existingAccount) {
      await existingAccount.destroy();
    }

    const account = await this.clerk.user.createExternalAccount({
      strategy: `oauth_${provider}`,
      redirectUrl: window.location.origin + '/a/settings/profile',
    });
    location.href = account.verification.externalVerificationRedirectURL.href;
  }

  public async disconnectSocialAccount(account: ExternalAccountResource) {
    await account.destroy();
    this.loader.remove('linkedAccounts');
  }

  public async addPasswordToSocialAccount(newPassword: string) {
    await this.clerk.user.updatePassword({ newPassword });
  }

  public async isSsoUser() {
    await this.init();
    if (!this.clerk) return false; // clerk failed to load
    return this.clerk.user.samlAccounts.length > 0;
  }
}
